import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import utc from 'dayjs/plugin/utc';
import React, { useState } from 'react';
import UserStatus from '../Users/components/UserStatus';
import { ColumnHeaderWithMenu } from '@src/components/Table/components/ColumnHeaderWithMenu';
import { SortDirection } from '@src/types/common';

dayjs.extend(utc);

export type actionType = 'openFilter';

interface RenderColumnsProps {
    t: TFunction;
    showLocalTime?: boolean;
    sorterState?: {
        sortBy: string | null;
        sortOrder: SortDirection | null;
        onSortChange: (columnKey: string, order: SortDirection | null) => void;
    };
    hiddenColumns: string[];
    setHiddenColumns: (keys: string[]) => void;
    onActionClick: (action: actionType, row: any) => void;
}

export function RenderColumns({
    t,
    showLocalTime,
    sorterState,
    hiddenColumns,
    setHiddenColumns,
    onActionClick,
}: RenderColumnsProps) {
    const baseColumns = [
        {
            key: 'date',
            dataIndex: 'date',
            title: t('pages.logs.dataTable.date'),
            sortable: true,
            render: (value: any) => {
                if (!value || value === '') return '---';

                const date = dayjs.utc(value);
                if (!date.isValid()) return value;

                return showLocalTime
                    ? date.local().format('YYYY-MM-DD HH:mm:ss')
                    : date.format('YYYY-MM-DD HH:mm:ss');
            },
        },
        {
            key: 'username',
            dataIndex: 'username',
            title: t('pages.logs.dataTable.username'),
            sortable: true,
        },
        {
            key: 'account',
            dataIndex: 'account',
            title: t('pages.logs.dataTable.account'),
            sortable: true,
        },
        {
            key: 'accessVia',
            dataIndex: 'accessVia',
            title: t('pages.logs.dataTable.accessVia'),
            sortable: true,
        },
        {
            key: 'ip',
            dataIndex: 'ip',
            title: t('pages.logs.dataTable.ip'),
            sortable: true,
        },
        {
            key: 'area',
            dataIndex: 'area',
            title: t('pages.logs.dataTable.area'),
            sortable: true,
        },
        {
            key: 'action',
            dataIndex: 'action',
            title: t('pages.logs.dataTable.action'),
            sortable: true,
        },
        {
            key: 'status',
            title: t('pages.logs.dataTable.status'),
            sortable: true,
            render: (row) => (
                <UserStatus
                    status={
                        typeof row.status === 'string'
                            ? row.status.toLowerCase()
                            : undefined
                    }
                />
            ),
        },
    ];

    const enhancedColumns = baseColumns.map((col) => ({
        ...col,
        title: (
            <ColumnHeaderWithMenu
                title={col.title}
                columnKey={col.key}
                allColumns={baseColumns}
                hiddenColumns={hiddenColumns}
                setHiddenColumns={setHiddenColumns}
                sortable={!!col.sortable}
                sortOrder={
                    sorterState?.sortBy === col.key
                        ? sorterState.sortOrder
                        : null
                }
                onSortChange={(order: SortDirection | null) =>
                    sorterState?.onSortChange(col.key, order)
                }
                onColumnMenuClick={(colKey, action) => {
                    switch (action) {
                        case 'hide':
                            if (!hiddenColumns.includes(colKey))
                                setHiddenColumns([...hiddenColumns, colKey]);
                            break;
                        case 'sortAsc':
                            sorterState?.onSortChange(colKey, 'ASC');
                            break;
                        case 'sortDesc':
                            sorterState?.onSortChange(colKey, 'DESC');
                            break;
                        case 'filter':
                            onActionClick('openFilter', null);
                            break;
                    }
                }}
            />
        ),
    }));

    return enhancedColumns.filter(
        (col) => !hiddenColumns.includes(col.key)
    ) as any;
}
